import {
  useCombobox,
  UseComboboxProps,
  UseComboboxState,
  UseComboboxStateChangeOptions,
} from "downshift";
import { useDownshiftEnvironment } from "../../../../internal/hooks";
import {
  SelectFieldGroupByValue,
  SelectFieldOption,
  SelectFieldPinnedOptions,
} from "../types";
import { useMemo, useState } from "react";
import {
  SectionMeta,
  SelectItem,
} from "../../../../internal/types/selectFieldInternalTypes";
import { useProcessedOptions } from "../../../../internal/hooks/useProcessedOptions";
import {
  buildSelectItems,
  toSelectItem,
} from "../../../../internal/functions/buildSelectItems";

export type { SectionMeta };

export type UseComboOptions = {
  // An array of currently loaded options
  options: SelectFieldOption[];
  // Pinned options configuration (raw prop)
  pinned?: SelectFieldPinnedOptions;
  // Function to convert a group value to a display label
  groupToString?: (groupValue: SelectFieldGroupByValue) => string;
  // Function to sort group sections
  groupSorter?: (
    a: SelectFieldGroupByValue,
    b: SelectFieldGroupByValue,
  ) => number;
  selectedOption: SelectFieldOption | null;
  onSelectedOptionChange: (option: SelectFieldOption | null) => void;
  displayAs: "popover" | "dialog";
  disableHighlightOnOpen?: boolean;
  disabled: boolean;
  virtualize?: boolean;
  onScrollToHighlight?: (index: number) => void;
} & Pick<UseComboboxProps<SelectItem>, "onInputValueChange" | "onIsOpenChange">;

/**
 * The purpose of useCombo is to provide an abstraction over the downshift combobox hook.
 */
export const useCombo = ({
  options,
  pinned,
  groupToString,
  groupSorter,
  selectedOption,
  onSelectedOptionChange,
  onInputValueChange,
  onIsOpenChange,
  displayAs = "popover",
  disableHighlightOnOpen = false,
  disabled,
  virtualize = false,
  onScrollToHighlight,
}: UseComboOptions) => {
  // Controlled input value state - needed so usePinnedOptions can access it
  // before useCombobox is called
  const [inputValue, setInputValue] = useState(selectedOption?.label ?? "");

  // Compute effective search value (empty if showing selected option's label)
  const effectiveSearchValue =
    selectedOption?.label === inputValue ? "" : inputValue;

  const { pinnedSections, ungroupedItems, groupSections } = useProcessedOptions(
    {
      options,
      pinned,
      searchValue: effectiveSearchValue,
      groupToString,
      groupSorter,
    },
  );

  const { items, sectionsMeta } = useMemo(
    () =>
      buildSelectItems({
        pinnedSections,
        groupSections,
        ungroupedItems,
      }),
    [pinnedSections, groupSections, ungroupedItems],
  );

  const selectedDownshiftItem = useMemo(() => {
    return selectedOption ? toSelectItem(selectedOption) : null;
  }, [selectedOption]);

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const {
    isOpen,
    highlightedIndex,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    closeMenu,
    openMenu,
    setHighlightedIndex,
  } = useCombobox({
    isItemDisabled: (item, _index) => {
      if (!item) {
        return false; // Downshift passes -1 index with an undefined item. Ignore it.
      }
      return (disabled || item.disabled) ?? false;
    },
    environment: environment,
    selectedItem: selectedDownshiftItem,
    inputValue, // Controlled input value
    onSelectedItemChange: ({
      selectedItem: si,
    }: {
      selectedItem: SelectItem;
    }) => {
      // Regular, pinned, and grouped options are all selectable
      if (
        si &&
        (si.type === "option" ||
          si.type === "pinned-option" ||
          si.type === "grouped-option")
      ) {
        onSelectedOptionChange(si.original);
      } else {
        onSelectedOptionChange(null);
      }
    },
    onInputValueChange: (changes) => {
      // Update local state
      setInputValue(changes.inputValue ?? "");
      // Call external handler
      onInputValueChange?.(changes);
    },
    onHighlightedIndexChange: ({ type, highlightedIndex: idx }) => {
      if (type !== useCombobox.stateChangeTypes.ItemMouseMove && idx >= 0) {
        onScrollToHighlight?.(idx);
      }
    },
    onIsOpenChange: (changes) => {
      onIsOpenChange?.(changes);
      if (changes.isOpen && !disableHighlightOnOpen) {
        const selectedItemIndex = items.findIndex(
          (item) => item.original.id === selectedOption?.id,
        );
        setHighlightedIndex(selectedItemIndex);
      }
    },
    items: items,
    itemToString(item: SelectItem | null) {
      return item?.original.label ?? "";
    },
    stateReducer: (state, actionAndChanges) =>
      stateReducer(state, actionAndChanges, displayAs),
    ...(virtualize && { scrollIntoView: () => {} }),
  });

  return {
    inputValue,
    wrapperDivRef,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    isOpen,
    items,
    sectionsMeta,
    closeMenu,
    openMenu,
  };
};

function stateReducer(
  state: UseComboboxState<SelectItem>,
  actionAndChanges: UseComboboxStateChangeOptions<SelectItem>,
  displayAs: "popover" | "dialog",
) {
  const { type, changes } = actionAndChanges;
  // this prevents the menu from being closed when the user selects an item with 'Enter' or mouse
  switch (type) {
    case useCombobox.stateChangeTypes.InputBlur:
      // keep the dialog open if the user blurs the input while the dialog is open
      if (displayAs === "dialog" && state.isOpen) {
        return {
          ...changes,
          isOpen: state.isOpen,
        };
      }
      return {
        ...changes,
        inputValue: state.selectedItem?.original.label ?? state.inputValue,
      };
    case useCombobox.stateChangeTypes.InputKeyDownEscape:
    case useCombobox.stateChangeTypes.FunctionCloseMenu:
      return {
        ...changes,
        inputValue: state.selectedItem?.original.label ?? state.inputValue,
      };
    case useCombobox.stateChangeTypes.InputClick:
      if (displayAs === "dialog" && state.isOpen) {
        // keep the dialog open if the user clicks the input while the dialog is open
        return {
          ...changes,
          isOpen: state.isOpen,
        };
      }
      return changes;
    case useCombobox.stateChangeTypes.InputKeyDownEnter:
    case useCombobox.stateChangeTypes.ItemClick:
      return changes;
    default:
      return changes; // otherwise business as usual.
  }
}
